import React, { useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import { searchInventory, clearSearchResults } from '../../store/slices/inventorySlice';
import { LocationInfo } from '../../types/inventory';
import { Search, MapPin, Package, Loader2, AlertCircle } from 'lucide-react';

interface InventorySearchProps {
  onLocationFound: (location: LocationInfo) => void;
}

const InventorySearch: React.FC<InventorySearchProps> = ({ onLocationFound }) => {
  const dispatch = useAppDispatch();
  const { searchResults, searchLoading, searchError } = useAppSelector((state) => state.inventory);

  const [searchParams, setSearchParams] = useState({
    styleCode: '',
    color: '',
    size: ''
  });

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchParams.styleCode.trim()) {
      dispatch(searchInventory({
        style_code: searchParams.styleCode,
        color: searchParams.color || undefined,
        size: searchParams.size || undefined
      }));
    }
  };

  const handleClearResults = () => {
    dispatch(clearSearchResults());
    setSearchParams({ styleCode: '', color: '', size: '' });
  };

  const handleLocationSelect = (location: any) => {
    const locationInfo: LocationInfo = {
      location: location.location,
      type: location.type,
      current_capacity: 0, // This would come from the backend
      available_space: 0, // This would come from the backend
      styles: [] // This would come from the backend
    };
    onLocationFound(locationInfo);
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold text-gray-900">Inventory Search</h2>
        {searchResults.length > 0 && (
          <button
            onClick={handleClearResults}
            className="text-sm text-gray-500 hover:text-gray-700"
          >
            Clear Results
          </button>
        )}
      </div>

      {/* Search Form */}
      <form onSubmit={handleSearch} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Style Code *
            </label>
            <input
              type="text"
              placeholder="e.g. GFW23669"
              value={searchParams.styleCode}
              onChange={(e) => setSearchParams(prev => ({
                ...prev,
                styleCode: e.target.value.toUpperCase()
              }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Color (Optional)
            </label>
            <select
              value={searchParams.color}
              onChange={(e) => setSearchParams(prev => ({
                ...prev,
                color: e.target.value
              }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Colors</option>
              <option value="Black">Black</option>
              <option value="White">White</option>
              <option value="Blue">Blue</option>
              <option value="Red">Red</option>
              <option value="Green">Green</option>
              <option value="Beige">Beige</option>
              <option value="Khaki">Khaki</option>
              <option value="Wine">Wine</option>
              <option value="Navy">Navy</option>
              <option value="Grey">Grey</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Size (Optional)
            </label>
            <select
              value={searchParams.size}
              onChange={(e) => setSearchParams(prev => ({
                ...prev,
                size: e.target.value
              }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Sizes</option>
              <option value="UK 6">UK 6</option>
              <option value="UK 8">UK 8</option>
              <option value="UK 10">UK 10</option>
              <option value="UK 12">UK 12</option>
              <option value="UK 14">UK 14</option>
              <option value="UK 16">UK 16</option>
            </select>
          </div>
        </div>
        
        <div className="flex justify-center">
          <button
            type="submit"
            disabled={!searchParams.styleCode.trim() || searchLoading}
            className="inline-flex items-center px-6 py-2 bg-blue-600 text-white text-sm font-medium rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {searchLoading ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Search className="h-4 w-4 mr-2" />
            )}
            Search Inventory
          </button>
        </div>
      </form>

      {/* Error Display */}
      {searchError && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <AlertCircle className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Search Error</h3>
              <p className="text-sm text-red-700 mt-1">{searchError}</p>
            </div>
          </div>
        </div>
      )}

      {/* Loading State */}
      {searchLoading && (
        <div className="text-center py-8">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-blue-600" />
          <p className="text-gray-600 mt-2">Searching inventory...</p>
        </div>
      )}

      {/* Search Results */}
      {searchResults.length > 0 && !searchLoading && (
        <div className="space-y-4">
          <h3 className="text-lg font-medium text-gray-900">
            Search Results ({searchResults.length} items found)
          </h3>
          
          <div className="space-y-3">
            {searchResults.map((item, index) => (
              <div key={index} className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <h4 className="font-medium text-gray-900">{item.sku}</h4>
                    <p className="text-sm text-gray-600">{item.style_name}</p>
                    <div className="flex gap-4 text-sm text-gray-500 mt-1">
                      <span>Size: {item.size}</span>
                      <span>Color: {item.color}</span>
                      <span className="font-medium">Total Qty: {item.quantity}</span>
                    </div>
                  </div>
                  
                  <div className="flex items-center text-sm">
                    <Package className="h-4 w-4 text-gray-400 mr-1" />
                    <span className="text-gray-600">{item.locations.length} location(s)</span>
                  </div>
                </div>
                
                {/* Locations */}
                <div className="space-y-2">
                  <h5 className="text-sm font-medium text-gray-700">Locations:</h5>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                    {item.locations.map((location, locIndex) => (
                      <div
                        key={locIndex}
                        className="flex items-center justify-between p-3 bg-white border border-gray-200 rounded-md hover:bg-blue-50 cursor-pointer"
                        onClick={() => handleLocationSelect(location)}
                      >
                        <div className="flex items-center">
                          <MapPin className="h-4 w-4 text-blue-500 mr-2" />
                          <div>
                            <p className="text-sm font-medium text-gray-900">{location.location}</p>
                            <p className="text-xs text-gray-500 capitalize">{location.location_type}</p>
                          </div>
                        </div>
                        
                        <div className="text-right">
                          <p className="text-sm font-medium text-gray-900">{location.quantity}</p>
                          <p className="text-xs text-gray-500">pieces</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* No Results */}
      {searchResults.length === 0 && !searchLoading && searchParams.styleCode && !searchError && (
        <div className="text-center py-8">
          <Package className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Items Found</h3>
          <p className="text-gray-600">
            No inventory found for style code "{searchParams.styleCode}"
            {searchParams.color && ` in ${searchParams.color}`}
            {searchParams.size && ` size ${searchParams.size}`}
          </p>
        </div>
      )}
    </div>
  );
};

export default InventorySearch;
